import React, { useState } from 'react'
import { AlertTriangle, XCircle, FileText, AlertCircle } from 'lucide-react'
import { useExperimentalStore } from '@/lib/store/experimental'

export function ExperimentalFeatureModal() {
  const { showWarningModal, pendingFeature, acceptFeatureRisks, dismissWarning } = useExperimentalStore()
  const [checkboxes, setCheckboxes] = useState({
    understand: false,
    testnetOnly: false,
    noLiability: false
  })
  
  if (!showWarningModal || !pendingFeature) return null
  
  const allChecked = Object.values(checkboxes).every(v => v)
  const isCritical = pendingFeature.warningLevel === 'critical'
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`p-6 border-b ${
          isCritical 
            ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800' 
            : 'bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              {isCritical ? (
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400 mt-1" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-orange-600 dark:text-orange-400 mt-1" />
              )}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  ⚠️ EXPERIMENTAL FEATURE WARNING
                </h2>
                <p className="text-lg mt-1 text-gray-700 dark:text-gray-300">
                  {pendingFeature.name}
                </p>
              </div>
            </div>
            <button
              onClick={dismissWarning}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <span className="sr-only">Close</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <div>
            <p className="text-gray-700 dark:text-gray-300">
              {pendingFeature.description}
            </p>
          </div>
          
          {/* Critical Risks */}
          <div className={`p-4 rounded-lg ${
            isCritical 
              ? 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700' 
              : 'bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700'
          }`}>
            <h3 className="font-semibold text-red-800 dark:text-red-200 mb-3 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Critical Risks
            </h3>
            <ul className="space-y-2 text-sm">
              {pendingFeature.risks.map((risk, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-red-600 dark:text-red-400 mr-2">•</span>
                  <span className="text-red-700 dark:text-red-300">{risk}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Academic Papers */}
          {pendingFeature.academicPapers && pendingFeature.academicPapers.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Based on Academic Research
              </h3>
              <ul className="space-y-1 text-sm">
                {pendingFeature.academicPapers.map((paper, index) => (
                  <li key={index} className="text-blue-700 dark:text-blue-300">
                    • {paper}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Consent Checkboxes */}
          <div className="space-y-4 border-t pt-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Required Acknowledgements
            </h3>
            
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={checkboxes.understand}
                onChange={(e) => setCheckboxes({ ...checkboxes, understand: e.target.checked })}
                className="mt-1 rounded border-gray-300 text-quantum-600 focus:ring-quantum-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                I understand this is <strong>alpha software</strong> based on academic research 
                that has <strong>NOT been audited</strong> and may contain critical vulnerabilities
              </span>
            </label>
            
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={checkboxes.testnetOnly}
                onChange={(e) => setCheckboxes({ ...checkboxes, testnetOnly: e.target.checked })}
                className="mt-1 rounded border-gray-300 text-quantum-600 focus:ring-quantum-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                I will <strong>ONLY use testnet coins</strong> with this feature and understand 
                that using mainnet funds could result in <strong>permanent loss</strong>
              </span>
            </label>
            
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={checkboxes.noLiability}
                onChange={(e) => setCheckboxes({ ...checkboxes, noLiability: e.target.checked })}
                className="mt-1 rounded border-gray-300 text-quantum-600 focus:ring-quantum-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                I will not hold QKey, its developers, or contributors liable for any losses, 
                damages, or issues arising from the use of this experimental feature
              </span>
            </label>
          </div>
          
          {/* Additional Warning for Critical Features */}
          {isCritical && (
            <div className="p-4 bg-gray-900 text-white rounded-lg">
              <p className="text-sm font-mono">
                <strong>FINAL WARNING:</strong> This feature modifies core cryptographic 
                operations. Incorrect usage could compromise your entire wallet. Only proceed 
                if you fully understand the implications and have reviewed the source code.
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 dark:bg-gray-900 flex justify-between items-center">
          <button
            onClick={dismissWarning}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          
          <button
            onClick={() => acceptFeatureRisks(pendingFeature.id)}
            disabled={!allChecked}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              allChecked
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            {allChecked ? 'Enable at My Own Risk' : 'Complete All Acknowledgements'}
          </button>
        </div>
      </div>
    </div>
  )
}